import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useContent } from './ContentContext';
import { normalizeThemeOptions } from '../constants/themeOptions';

const ThemeContext = createContext(null);
const STORAGE_KEY = 'refuel-theme';

const getInitialTheme = () => {
  if (typeof window === 'undefined') return 'original';
  const saved = window.localStorage.getItem(STORAGE_KEY);
  return saved || 'original';
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(getInitialTheme);
  const { content, loaded } = useContent();
  const themes = useMemo(() => normalizeThemeOptions(content.site?.themeOptions), [content.site?.themeOptions]);
  const activeTheme = themes.find((option) => option.value === theme);

  useEffect(() => {
    if (!activeTheme) {
      if (loaded) setTheme('original');
      return;
    }
    const root = document.documentElement;
    if (activeTheme.value !== theme) setTheme(activeTheme.value);
    root.setAttribute('data-theme', activeTheme.value);
    root.style.setProperty('--app-red', activeTheme.color);
    root.style.setProperty('--app-red-strong', `color-mix(in srgb, ${activeTheme.color} 78%, white)`);
    root.colorScheme = activeTheme.value === 'light' ? 'light' : 'dark';
    try {
      window.localStorage.setItem(STORAGE_KEY, activeTheme.value);
    } catch (error) {
      console.warn('Could not save theme preference', error);
    }
  }, [activeTheme, loaded, theme]);

  const value = useMemo(() => ({
    theme,
    setTheme,
    themes,
    activeTheme,
    isLight: theme !== 'original',
    modeLabel: activeTheme?.label,
  }), [theme, themes, activeTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
